import { useCallback, useRef, useEffect, useMemo, PropsWithChildren } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, TIconName } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { selectProfileByUserId } from '@/store/selectors';
import { TDashboardConfigSettingsUsersTab } from '@/store/models/dashboard-config.model';
import { ITab } from '@/store/models/profile.model';

import { NavigationTabs } from '@/ui-kit/navigation-tabs/NavigationTabs';

import { ProfileProvider } from '@/features/context/ProfileContext';
import { ProfileHeader } from '@/features/user/components/ProfileHeader';
import { useKycStatus } from '@/features/kyc/hooks/useKycStatus';
import { useUserInfo } from '@/features/user/hooks/useUserInfo';

const ProfileTabIcons: Record<TDashboardConfigSettingsUsersTab, TIconName> = {
  overview: 'account_circle',
  kyckyb: 'contact_mail',
  wallets: 'account_balance_wallet',
  cards: 'credit_card',
  beneficiaries: 'groups',
  children: 'account_tree',
  companyMembers: 'account_tree',
};

export const Profile = ({ children }: PropsWithChildren<{}>) => {
  const params = useParams();
  const userId = params.userId;
  const { t } = useTranslation();
  const config = useDashboardConfig();

  const user = useAppSelector(
    (state) => userId?.isNumber() && selectProfileByUserId(state, userId.toNumber())?.state?.user.state,
  );

  const isLoading = useAppSelector((state) => state?.profile?.profiles?.isLoading);

  const { kycStatus } = useKycStatus(user);

  const containerRef = useRef(null);
  const currentPath = params['*'];

  useEffect(() => {
    // Scroll to the top of the page whenever the tab changes
    if (currentPath !== undefined && containerRef?.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentPath]);

  const { isPhysicalUser, isSelfEmployed, isKyb } = useUserInfo(user);

  const shouldHideChildrenTab = !isPhysicalUser || Boolean(isPhysicalUser && user?.parentUserId) || isSelfEmployed;

  const shouldHideKycKybTab = useMemo(() => kycStatus === 'not_required', [kycStatus]);

  const getTabLabelKey = useCallback(
    (currentTab: TDashboardConfigSettingsUsersTab) => {
      if (currentTab === 'kyckyb') return isKyb ? 'kyb' : 'kyc';
      return currentTab;
    },
    [isKyb],
  );

  const tabs =
    config.settings.users?.tabs.reduce((acc: ITab[], currentTab) => {
      if (currentTab === 'beneficiaries' && !config.settings.beneficiaries?.length) {
        return acc;
      }

      if (currentTab === 'children' && shouldHideChildrenTab) {
        return acc;
      }

      if (currentTab === 'companyMembers') {
        if (isPhysicalUser && !isSelfEmployed) return acc;
        return [
          ...acc,
          {
            id: 'company-members',
            path: 'company-members',
            label: t(`pages.currentProfiles.profile.tabs.title.companyMembers`),
            icon: ProfileTabIcons['companyMembers'],
            testId: 'tab-company-members',
          },
        ];
      }

      if (currentTab === 'kyckyb' && shouldHideKycKybTab) {
        return acc;
      }

      return [
        ...acc,
        {
          id: currentTab,
          path: currentTab,
          label: t(`pages.currentProfiles.profile.tabs.title.${getTabLabelKey(currentTab)}`),
          icon: ProfileTabIcons[currentTab],
          testId: `tab-${currentTab}`,
        },
      ];
    }, []) ?? [];

  if (isLoading && !user)
    return (
      <div className="flex items-center justify-center h-[calc(100vh-160px)]">
        <Loader size="xl" />
      </div>
    );
  if (!user) return null;

  return (
    <ProfileProvider user={user}>
      <div className="flex flex-col h-[calc(100vh-160px)]">
        <div className="flex flex-col gap-6">
          <ProfileHeader />
          <NavigationTabs items={tabs} />
        </div>
        <div className="overflow-auto" ref={containerRef}>
          {children}
        </div>
      </div>
    </ProfileProvider>
  );
};
